import { getAuth } from "@clerk/react-router/server";
import { clsx, type ClassValue } from "clsx";
import { LoaderFunctionArgs, redirect } from "react-router";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function authorize(args: LoaderFunctionArgs) {
  const { sessionClaims } = await getAuth(args);
  if (!sessionClaims || !sessionClaims.org_slug) {
    throw redirect("/sign-in");
  }
  return {
    org_slug: sessionClaims.org_slug,
    primaryEmail: sessionClaims.primaryEmail as string,
  };
}

export async function authorizeInternal(args: LoaderFunctionArgs) {
  const { sessionClaims } = await getAuth(args);
  if (!sessionClaims || !sessionClaims.org_slug) {
    throw redirect("/sign-in");
  }

  // Internal access is explicitly scoped to RB2 or server-managed internal metadata.
  // Do not treat generic org admin role as internal.
  const publicMetadata = sessionClaims.publicMetadata as
    | Record<string, unknown>
    | undefined;
  const isInternal =
    publicMetadata?.isInternal === true ||
    sessionClaims.org_slug === "rb2"; // rb2 organization members are internal

  if (!isInternal) {
    throw redirect("/"); // Redirect to dashboard if not internal
  }

  return {
    org_slug: sessionClaims.org_slug,
    primaryEmail: sessionClaims.primaryEmail as string,
    isInternal: true,
  };
}

// Retry utility for API calls with exponential backoff
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: {
    retries?: number;
    delay?: number;
    backoff?: number;
    onRetry?: (error: Error, attempt: number) => void;
  } = {},
): Promise<T> {
  const { retries = 3, delay = 1000, backoff = 2, onRetry } = options;

  let lastError: Error;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < retries) {
        const waitTime = delay * Math.pow(backoff, attempt);

        if (onRetry) {
          onRetry(lastError, attempt + 1);
        }

        await new Promise((resolve) => setTimeout(resolve, waitTime));
      }
    }
  }

  throw lastError!;
}

// Error class for better error handling
export class ServiceDeskError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ServiceDeskError";
  }
}

// Type guard for error checking
export function isServiceDeskError(error: unknown): error is ServiceDeskError {
  return error instanceof ServiceDeskError;
}
